import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'moment/locale/it';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, Clock, User, X, MessageCircle } from 'lucide-react';
import axios from 'axios';
import ChatModal from './ChatModal';
import RescheduleColloquioModal from './RescheduleColloquioModal';

moment.locale('it');
const localizer = momentLocalizer(moment);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CalendarioColloqui = ({ agencyId, userType = 'agency' }) => {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [matches, setMatches] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedRichiestaId, setSelectedRichiestaId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [colloquioToReschedule, setColloquioToReschedule] = useState(null);
  const [formData, setFormData] = useState({
    match_id: '',
    date: '',
    time: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
    // Carica userId dal token o localStorage
    const storedUserId = localStorage.getItem('user_id');
    if (storedUserId) {
      setUserId(parseInt(storedUserId));
    }
  }, [agencyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      const headers = token ? {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      } : {};
      
      // Converti agencyId in numero
      const numericAgencyId = typeof agencyId === 'string' && !isNaN(agencyId) ? parseInt(agencyId) : agencyId;
      
      // Load matches con parametro agency_id o candidate_id
      const queryParam = userType === 'agency' ? `agency_id=${numericAgencyId}` : `candidate_id=${numericAgencyId}`;
      const matchesRes = await axios.get(`${API}/v1/matches?${queryParam}`, { headers });
      console.log('Matches for', userType, numericAgencyId, ':', matchesRes.data);
      
      setMatches(matchesRes.data);

      // Convert matches with interview_date to calendar events
      const events = matchesRes.data
        .filter(m => m.interview_date)
        .map(m => ({
          id: m.id,
          title: userType === 'agency' 
            ? `Colloquio con ${m.candidate_name || 'Candidato'}`
            : `Colloquio con ${m.agency_name || 'Agenzia'}`,
          start: new Date(m.interview_date),
          end: new Date(new Date(m.interview_date).getTime() + 60 * 60 * 1000), // 1 hour duration
          resource: m,
          status: m.status
        }));

      console.log('Calendar events:', events);
      setInterviews(events);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Errore nel caricamento del calendario');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSlot = (slotInfo) => {
    // Sia agenzie che candidati possono programmare colloqui
    setSelectedSlot(slotInfo);
    setFormData({
      match_id: '',
      date: moment(slotInfo.start).format('YYYY-MM-DD'),
      time: moment(slotInfo.start).format('HH:mm'),
      notes: ''
    });
    setShowModal(true);
  };

  const handleSelectEvent = (event) => {
    setSelectedInterview(event.resource);
    // Se userType è 'agenzia', apri il modale per spostare
    if (userType === 'agenzia' || userType === 'agency') {
      setColloquioToReschedule(event.resource);
      setShowRescheduleModal(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.match_id) {
      toast.error('Seleziona un candidato');
      return;
    }

    try {
      const interviewDateTime = `${formData.date}T${formData.time}:00`;
      
      await axios.post(`${API}/v1/matches/${formData.match_id}/schedule-interview`, {
        interview_date: new Date(interviewDateTime).toISOString()
      });

      toast.success('Colloquio programmato con successo!');
      setShowModal(false);
      setFormData({ match_id: '', date: '', time: '', notes: '' });
      loadData();
    } catch (error) {
      console.error('Error scheduling interview:', error);
      toast.error('Errore nella programmazione del colloquio');
    }
  };

  const eventStyleGetter = (event) => {
    let backgroundColor = '#3b82f6'; // blue
    
    if (event.status === 'colloquio_effettuato') {
      backgroundColor = '#10b981'; // green
    } else if (event.status === 'in_attesa_feedback') {
      backgroundColor = '#f59e0b'; // amber
    } else if (event.status === 'chiuso') {
      backgroundColor = '#6b7280'; // gray
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '5px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block'
      }
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Available matches for scheduling (not yet scheduled)
  // Mostra tutti i match che non hanno ancora un colloquio programmato
  const availableMatches = matches.filter(m => !m.interview_date && m.match_type === 'esplicito');
  
  console.log('Available matches for scheduling (richieste senza data):', availableMatches);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="w-6 h-6 text-blue-600" />
            Calendario Colloqui
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Legend */}
          <div className="flex flex-wrap gap-4 mb-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-500 rounded"></div>
              <span>Programmato</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded"></div>
              <span>Effettuato</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-amber-500 rounded"></div>
              <span>In Attesa Feedback</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-500 rounded"></div>
              <span>Chiuso</span>
            </div>
          </div>

          {/* Calendar */}
          <div style={{ height: 600 }}>
            <Calendar
              localizer={localizer}
              events={interviews}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%' }}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              selectable={true}
              eventPropGetter={eventStyleGetter}
              messages={{
                next: 'Successivo',
                previous: 'Precedente',
                today: 'Oggi',
                month: 'Mese',
                week: 'Settimana',
                day: 'Giorno',
                agenda: 'Agenda',
                date: 'Data',
                time: 'Ora',
                event: 'Evento',
                noEventsInRange: 'Nessun colloquio in questo periodo'
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Schedule Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Programma Colloquio</CardTitle>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="match_id">
                    {userType === 'agency' ? 'Candidato *' : 'Agenzia *'}
                  </Label>
                  <select
                    id="match_id"
                    value={formData.match_id}
                    onChange={(e) => setFormData({ ...formData, match_id: e.target.value })}
                    className="w-full mt-1 p-2 border rounded-md"
                    required
                  >
                    <option value="">
                      {userType === 'agency' ? 'Seleziona candidato' : 'Seleziona agenzia'}
                    </option>
                    {availableMatches.map(match => (
                      <option key={match.id} value={match.id}>
                        {userType === 'agency' 
                          ? (match.candidate_name || 'Candidato N/A')
                          : (match.agency_name || 'Agenzia N/A')
                        }
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date">Data *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="time">Ora *</Label>
                    <Input
                      id="time"
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">Note (opzionale)</Label>
                  <textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full mt-1 p-2 border rounded-md"
                    rows="3"
                    placeholder="Aggiungi note sul colloquio..."
                  />
                </div>

                <div className="flex gap-2 justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowModal(false)}
                  >
                    Annulla
                  </Button>
                  <Button type="submit" className="bg-blue-600">
                    Conferma Colloquio
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Interview Details Modal (optional, for viewing event details) */}
      {selectedInterview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Dettagli Colloquio</CardTitle>
              <button
                onClick={() => setSelectedInterview(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-500" />
                <span className="font-medium">
                  {userType === 'agency' 
                    ? selectedInterview.candidate_name 
                    : selectedInterview.agency_name}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-gray-500" />
                <span>
                  {selectedInterview.interview_date 
                    ? moment(selectedInterview.interview_date).format('DD/MM/YYYY HH:mm')
                    : 'Data non disponibile'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500" />
                <span className="capitalize">{selectedInterview.status?.replace('_', ' ')}</span>
              </div>
              
              {/* Pulsante Chat */}
              <Button
                onClick={() => {
                  setSelectedRichiestaId(selectedInterview.id);
                  setChatOpen(true);
                  setSelectedInterview(null);
                }}
                className="w-full mt-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                Apri Chat
              </Button>
              
              <Button
                onClick={() => setSelectedInterview(null)}
                variant="outline"
                className="w-full"
              >
                Chiudi
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Chat Modal */}
      <ChatModal
        isOpen={chatOpen}
        onClose={() => {
          setChatOpen(false);
          setSelectedRichiestaId(null);
        }}
        richiestaId={selectedRichiestaId}
        userType={userType === 'agency' ? 'agenzia' : 'candidato'}
        userId={userId}
      />

      {/* Reschedule Modal */}
      <RescheduleColloquioModal
        isOpen={showRescheduleModal}
        onClose={() => {
          setShowRescheduleModal(false);
          setColloquioToReschedule(null);
        }}
        colloquio={colloquioToReschedule}
        onSuccess={() => {
          loadData();
          toast.success('Colloquio spostato con successo!');
        }}
      />
    </div>
  );
};

export default CalendarioColloqui;
